import { i18n } from '@/i18n'
import type { ContentLocale, LocalizedFields, Translated } from '@/types/api'

/** Picks the record name in the active content locale, falling back to EN, then any. */
export function pickName(record: Partial<Translated> | null | undefined, locale: ContentLocale): string {
  if (!record) return ''
  return (
    record[`name_${locale}`] ||
    record.name_en ||
    record.name_ru ||
    record.name_uz ||
    record.name_cyr ||
    ''
  )
}

/** Picks a flat localized label of a joined record (`${prefix}_en` … `${prefix}_cyr`) with pickName's fallbacks. */
export function pickLocalized<Prefix extends string>(
  record: Partial<LocalizedFields<Prefix>> | null | undefined,
  prefix: Prefix,
  locale: ContentLocale,
): string {
  if (!record) return ''
  const value = record as Partial<Record<string, string>>
  return pickName(
    { name_en: value[`${prefix}_en`], name_ru: value[`${prefix}_ru`], name_uz: value[`${prefix}_uz`], name_cyr: value[`${prefix}_cyr`] },
    locale,
  )
}

// Intl formatters are costly to build; keep one per locale and pattern.
const formatters = new Map<string, Intl.DateTimeFormat | Intl.NumberFormat>()

function formatter<T extends Intl.DateTimeFormat | Intl.NumberFormat>(name: string, make: (tag: string) => T): T {
  const key = `${i18n.tag}:${name}`
  let cached = formatters.get(key) as T | undefined
  if (!cached) {
    try {
      cached = make(i18n.tag)
    } catch {
      cached = make('en-GB')
    }
    formatters.set(key, cached)
  }
  return cached
}

/** Every date in the console is numeric ("24.09.2026 20:15"); month names are never spelled out. */
export function formatDateTime(value?: string | null) {
  return formatShortDateTime(value)
}

export function formatDate(value?: string | null) {
  return formatShortDate(value)
}

/** True for an API decimal that is zero in any spelling: "0", "0.000", "-0.00". */
export function isZeroDecimal(value: string) {
  return /^-?0*(?:\.0*)?$/.test(value.trim()) && /\d/.test(value)
}

/**
 * Groups the integer part of an API decimal string without converting it to a
 * binary float, so "12345678.125" never turns into 12345678.124999. Zero is
 * kept: chart axes and other places where "0" itself must show use this.
 */
export function groupDecimal(value: string, fractionDigits?: number) {
  const match = /^(-?)(\d+)(?:\.(\d+))?$/.exec(value.trim())
  if (!match) return value
  const [, sign, int, frac = ''] = match
  // A full-width no-break space: groups stay clearly apart ("10 000 000 000") and a large amount
  // never wraps across lines. (The narrow U+202F renders almost invisibly in the interface fonts.)
  const grouped = int.replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0')
  const fraction = fractionDigits === undefined ? frac : frac.padEnd(fractionDigits, '0').slice(0, fractionDigits)
  return `${sign}${grouped}${fraction ? `.${fraction}` : ''}`
}

/**
 * A decimal for display: grouped, and "—" for an empty value or an exact zero
 * (client: zero shows as a dash). The check is on the value itself, so a
 * non-zero value cut to zero by `fractionDigits` ("0.0004", 3) shows "0.000".
 */
export function formatDecimal(value?: string | null, fractionDigits?: number) {
  if (value === null || value === undefined || value === '' || isZeroDecimal(value)) return '—'
  return groupDecimal(value, fractionDigits)
}

/** Rounds an API decimal string half away from zero without a float: ("-12.345", 2) → "-12.35"; a result of zero is unsigned. */
export function roundDecimal(value: string, digits: number) {
  const match = /^(-?)(\d+)(?:\.(\d*))?$/.exec(value.trim())
  if (!match) return value
  const [, sign, int, frac = ''] = match
  const padded = frac.padEnd(digits + 1, '0')
  let units = BigInt(int + padded.slice(0, digits))
  if (Number(padded[digits]) >= 5) units += 1n
  const text = units.toString().padStart(digits + 1, '0')
  const rounded = digits ? `${text.slice(0, -digits)}.${text.slice(-digits)}` : text
  return units === 0n ? rounded : `${sign}${rounded}`
}

/**
 * A value rounded to `digits` for display. Only an exact zero (0, -0, "0.00")
 * or an empty value shows "—"; a non-zero value that rounds to zero shows the
 * rounded zero ("0", "0.000"), never the dash (client 2026-10-24: «Факат мутлак
 * 0 -> "-"»). With `trim` the trailing zeros go, as formatNumber does.
 */
export function formatFixed(value: string | number | null | undefined, digits: number, { trim = false } = {}) {
  if (value === null || value === undefined || value === '') return '—'
  if (typeof value === 'number' ? value === 0 : isZeroDecimal(value)) return '—'
  let rounded = typeof value === 'number' ? value.toFixed(digits) : roundDecimal(value, digits)
  // toFixed keeps the sign of a small negative ("-0.00"): a rounded zero is just zero.
  if (rounded.startsWith('-') && isZeroDecimal(rounded)) rounded = rounded.slice(1)
  return trim ? groupDecimal(trimDecimal(rounded)) : groupDecimal(rounded, digits)
}

export { trimDecimal, sumDecimals, multiplyDecimals, divideDecimals, pricePerTonne, pricePerKilogram } from './decimal.ts'
import { trimDecimal } from './decimal.ts'

/** A decimal as Ucode showed it: grouped thousands and no trailing zeros ("28 000", "5.5"); "—" only for an exact zero. */
export function formatNumber(value?: string | null) {
  if (value === null || value === undefined || value === '') return '—'
  return formatDecimal(trimDecimal(value))
}

const pad = (part: number) => String(part).padStart(2, '0')

/** "24.09.2026 20:15" in the viewer's time zone; one line in every table. */
export function formatShortDateTime(value?: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/** "24.09.2026"; calendar dates (YYYY-MM-DD) are read as written, not shifted by time zone. */
export function formatShortDate(value?: string | null) {
  if (!value) return '—'
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (day) return `${day[3]}.${day[2]}.${day[1]}`
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
}

export function formatCount(value: number) {
  return formatter('count', (tag) => new Intl.NumberFormat(tag)).format(value)
}

export function initials(name: string) {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join('') || '·'
  )
}

export function shortId(id?: string | null) {
  return id ? id.slice(0, 8) : '—'
}


